import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Import,
  PenLine,
  Plus,
  Sparkles,
  Trash2,
  User,
  Users,
  Wand2,
} from 'lucide-react';
import type { PlatformId, StyleProfile, WritingSample, WritingSampleSource } from '../../shared/types';
import { PLATFORM_LIST } from '../../shared/platforms';
import { PageHeader } from '../components/layout';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  Input,
  Progress,
  Select,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '../components/ui';
import { StyleAttributeList } from '../components/style/StyleAttributeList';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { formatRelative } from '../lib/format';
import { useSessionStore } from '../stores/session';
import { useWorkspaceStore } from '../stores/workspace';

const SOURCE_META: Record<WritingSampleSource, { label: string; tone: 'neutral' | 'info' | 'accent'; icon: typeof PenLine }> = {
  manual: { label: 'Manual', tone: 'neutral', icon: PenLine },
  import: { label: 'Imported', tone: 'info', icon: Import },
  edit_learned: { label: 'Learned from edits', tone: 'accent', icon: Sparkles },
};

/* ================================================================== */
/* Add sample dialog                                                   */
/* ================================================================== */

function AddSampleDialog({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}) {
  const { toast } = useToast();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [platform, setPlatform] = useState<PlatformId | ''>('');

  const reset = () => {
    setContent('');
    setTitle('');
    setPlatform('');
  };

  const add = useMutation({
    mutationFn: () =>
      api.style.addSample({
        content: content.trim(),
        title: title.trim() || undefined,
        platform: platform || undefined,
        source: 'manual',
      }),
    onSuccess: () => {
      onAdded();
      reset();
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: 'Could not save that sample',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const tooShort = content.trim().length < 40;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!add.isPending) {
          onOpenChange(next);
          if (!next) reset();
        }
      }}
      title="Add a writing sample"
      description="Paste a past post, newsletter blurb or email — anything that sounds like you."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={add.isPending}>
            Cancel
          </Button>
          <Button
            leftIcon={<Plus className="size-4" />}
            loading={add.isPending}
            disabled={tooShort}
            onClick={() => add.mutate()}
          >
            Add sample
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Content" htmlFor="sample-dialog-content" required>
          <Textarea
            id="sample-dialog-content"
            rows={7}
            maxRows={16}
            autoFocus
            placeholder="Our new Ethiopian lot landed this morning and honestly it smells like blueberry jam..."
            value={content}
            onChange={(event) => setContent(event.target.value)}
          />
          {content.trim().length > 0 && tooShort && (
            <p className="mt-1.5 text-xs text-subtle">A little more — at least 40 characters to be useful.</p>
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Title" htmlFor="sample-dialog-title" hint="Optional — helps you find it later.">
            <Input
              id="sample-dialog-title"
              placeholder="Spring newsletter intro"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <Field label="Platform" htmlFor="sample-dialog-platform" hint="Optional — if this sample was written for one.">
            <Select
              id="sample-dialog-platform"
              value={platform}
              onChange={(event) => setPlatform(event.target.value as PlatformId | '')}
              placeholder="Any platform"
              options={PLATFORM_LIST.map((spec) => ({ value: spec.id, label: spec.shortName }))}
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

/* ================================================================== */
/* Sample row                                                          */
/* ================================================================== */

function SampleRow({ sample, onDelete }: { sample: WritingSample; onDelete: () => void }) {
  const meta = SOURCE_META[sample.source];
  const Icon = meta.icon;
  return (
    <Card className="flex items-start gap-3 px-4 py-3">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-elevated text-muted">
        <Icon className="size-3.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-medium text-fg">{sample.title ?? 'Writing sample'}</p>
          <Badge tone={meta.tone} size="sm">
            {meta.label}
          </Badge>
          {sample.platform && (
            <Badge tone="neutral" size="sm">
              {sample.platform}
            </Badge>
          )}
        </div>
        <p className="pap-clamp-2 text-xs leading-relaxed text-muted">{sample.content}</p>
        <p className="text-[11px] text-subtle">Added {formatRelative(sample.createdAt)}</p>
      </div>
      <Button variant="ghost" size="icon-sm" aria-label="Delete sample" onClick={onDelete}>
        <Trash2 className="size-3.5" />
      </Button>
    </Card>
  );
}

/* ================================================================== */
/* Profile panel                                                       */
/* ================================================================== */

function ProfilePanel({ profile }: { profile: StyleProfile }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-fg">{profile.name}</p>
          {profile.summary && <p className="mt-1 text-sm leading-relaxed text-muted">{profile.summary}</p>}
        </div>
        <Badge tone="neutral">v{profile.version}</Badge>
      </div>

      <StyleAttributeList attributes={profile.attributes} />

      {profile.exemplars.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-subtle">Exemplars</p>
          <ul className="space-y-2">
            {profile.exemplars.map((exemplar, index) => (
              <li
                key={index}
                className="rounded-lg border border-dashed border-border bg-elevated/30 p-3 text-sm italic leading-relaxed text-muted"
              >
                “{exemplar}”
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Page                                                                 */
/* ================================================================== */

/**
 * Writing style — owned by WEB-4. Samples manager (paste dialog + list with
 * source badges), "Analyze my style" CTA, and the resulting profile with
 * attribute chips, summary, exemplars and version — split into per-user vs
 * workspace tabs when both exist.
 */
export function StylePage() {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const user = useSessionStore((state) => state.user);
  const aiProvider = useSessionStore((state) => state.aiProvider);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WritingSample | null>(null);
  const [scope, setScope] = useState<'workspace' | 'user'>('workspace');
  const [activeTab, setActiveTab] = useState<'workspace' | 'user'>('workspace');

  const samplesQuery = useQuery({
    queryKey: qk.styleSamples(workspaceId),
    queryFn: () => api.style.samples(),
    enabled: Boolean(workspaceId),
  });

  const profileQuery = useQuery({
    queryKey: qk.styleProfile(workspaceId),
    queryFn: () => api.style.profile(),
    enabled: Boolean(workspaceId),
  });

  const samples = samplesQuery.data?.samples ?? [];
  const profiles = profileQuery.data?.profiles ?? [];
  const workspaceProfile = profiles.find((p) => p.userId === null) ?? null;
  const userProfile = profiles.find((p) => p.userId === user?.id) ?? null;
  const showTabs = Boolean(workspaceProfile && userProfile);

  const invalidateSamples = () =>
    void queryClient.invalidateQueries({ queryKey: qk.styleSamples(workspaceId) });

  const removeSample = useMutation({
    mutationFn: (id: string) => api.style.deleteSample(id),
    onSuccess: () => {
      invalidateSamples();
      toast({ title: 'Sample removed' });
    },
    onError: (error) => {
      toast({
        title: 'Could not remove that sample',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const analyze = useMutation({
    mutationFn: () => api.style.analyze({ scope }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: qk.styleProfile(workspaceId) });
      setActiveTab(response.profile.userId ? 'user' : 'workspace');
      toast({
        title: 'Style profile updated',
        description: `Learned from ${response.sampleCount} sample${response.sampleCount === 1 ? '' : 's'}.`,
        tone: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Style analysis failed',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  if (!workspaceId) return null;

  const activeProfile = activeTab === 'user' ? userProfile : workspaceProfile;

  return (
    <>
      <PageHeader
        title="Writing style"
        description="Add writing samples and we learn your voice — then every draft is written in it."
        actions={
          <Button leftIcon={<Plus className="size-4" />} onClick={() => setAddOpen(true)}>
            Add sample
          </Button>
        }
      />

      {aiProvider === 'mock' && (
        <Alert tone="warning" title="Demo AI is active" className="mb-6">
          No Gemini credentials are configured, so style analysis comes from a deterministic mock —
          still a real, structured profile, just not model-generated.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-fg">Writing samples</h2>
          {samplesQuery.isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : samplesQuery.isError ? (
            <Alert tone="danger" title="Could not load your writing samples">
              {samplesQuery.error instanceof ApiError
                ? samplesQuery.error.message
                : 'Try refreshing the page.'}
            </Alert>
          ) : samples.length === 0 ? (
            <EmptyState
              icon={<Sparkles className="size-5" />}
              title="No writing samples yet"
              description="Paste a few posts or emails you have written. We analyze them into a voice profile you can review and edit."
              action={
                <Button leftIcon={<Plus className="size-4" />} onClick={() => setAddOpen(true)}>
                  Add your first sample
                </Button>
              }
            />
          ) : (
            <ul className="space-y-2">
              {samples.map((sample) => (
                <li key={sample.id}>
                  <SampleRow sample={sample} onDelete={() => setDeleteTarget(sample)} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-4">
          <Card className="space-y-3 p-4">
            <div className="flex items-center gap-2">
              <Wand2 className="size-4 text-accent" aria-hidden />
              <h2 className="text-sm font-semibold text-fg">Analyze my style</h2>
            </div>
            <p className="text-xs leading-relaxed text-muted">
              Turns your samples into a structured voice profile — tone, sentence style, emoji use,
              calls to action — that every AI draft follows.
            </p>
            <div className="flex gap-1.5 rounded-lg border border-border bg-elevated p-1">
              <button
                type="button"
                onClick={() => setScope('workspace')}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                  scope === 'workspace' ? 'bg-surface text-fg shadow-hair' : 'text-muted hover:text-fg'
                }`}
              >
                <Users className="size-3.5" aria-hidden />
                Workspace
              </button>
              <button
                type="button"
                onClick={() => setScope('user')}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                  scope === 'user' ? 'bg-surface text-fg shadow-hair' : 'text-muted hover:text-fg'
                }`}
              >
                <User className="size-3.5" aria-hidden />
                Just me
              </button>
            </div>
            <Button
              fullWidth
              disabled={samples.length === 0}
              loading={analyze.isPending}
              onClick={() => analyze.mutate()}
            >
              {profiles.length > 0 ? 'Re-analyze my style' : 'Analyze my style'}
            </Button>
            {analyze.isPending && <Progress value={0} indeterminate size="sm" />}
          </Card>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        <h2 className="text-sm font-semibold text-fg">Style profile</h2>
        {profileQuery.isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : profileQuery.isError ? (
          <Alert tone="danger" title="Could not load your style profile">
            {profileQuery.error instanceof ApiError
              ? profileQuery.error.message
              : 'Try refreshing the page.'}
          </Alert>
        ) : !workspaceProfile && !userProfile ? (
          <EmptyState
            variant="plain"
            icon={<Check className="size-5" />}
            title="No profile yet"
            description="Analyze your samples above to generate a voice profile."
          />
        ) : showTabs ? (
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'workspace' | 'user')}>
            <TabsList>
              <TabsTrigger value="workspace">
                <Users className="size-3.5" /> Workspace default
              </TabsTrigger>
              <TabsTrigger value="user">
                <User className="size-3.5" /> Mine
              </TabsTrigger>
            </TabsList>
            <TabsContent value="workspace">
              {workspaceProfile && <ProfilePanel profile={workspaceProfile} />}
            </TabsContent>
            <TabsContent value="user">{userProfile && <ProfilePanel profile={userProfile} />}</TabsContent>
          </Tabs>
        ) : (
          activeProfile && <ProfilePanel profile={activeProfile} />
        )}
      </div>

      <AddSampleDialog open={addOpen} onOpenChange={setAddOpen} onAdded={invalidateSamples} />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(next) => !next && setDeleteTarget(null)}
        title="Remove this sample?"
        destructive
        confirmLabel="Remove"
        onConfirm={async () => {
          if (!deleteTarget) return;
          await removeSample.mutateAsync(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

export default StylePage;
